import { createTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { brand, radius, shadow } from './tokens';

const HEADING_FONT = '"Playfair Display", "Raleway", Georgia, serif';
const BODY_FONT = '"Poppins", "Arimo", system-ui, -apple-system, sans-serif';

const theme: Theme = createTheme({
  palette: {
    primary: {
      main: brand.gold,
      dark: brand.goldDark,
      light: brand.goldLight,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brand.cream,
      dark: brand.creamDark,
      light: '#FAF7F6',
      contrastText: brand.ink,
    },
    background: {
      default: brand.cream,
      paper: brand.white,
    },
    text: {
      primary: brand.ink,
      secondary: brand.inkSoft,
    },
    success: { main: brand.success, light: brand.successSoft, contrastText: '#FFFFFF' },
    warning: { main: brand.warning, light: brand.warningSoft, contrastText: '#3d2c00' },
    error: { main: brand.danger, light: brand.dangerSoft, contrastText: '#FFFFFF' },
    info: { main: brand.info, light: brand.infoSoft, contrastText: '#FFFFFF' },
    divider: brand.border,
    // Custom brand keys (see src/types/theme.d.ts)
    tertiary: {
      main: brand.ink,
      contrastText: '#FFFFFF',
    },
    accent: {
      sage: brand.sage,
      mauve: brand.mauve,
      rust: brand.rust,
      navy: brand.navy,
    },
  },
  shape: {
    borderRadius: radius.md,
  },
  typography: {
    fontFamily: BODY_FONT,
    h1: { fontFamily: HEADING_FONT, fontWeight: 700, fontSize: 'clamp(1.9rem, 5vw, 2.6rem)', lineHeight: 1.15, color: brand.ink },
    h2: { fontFamily: HEADING_FONT, fontWeight: 700, fontSize: 'clamp(1.6rem, 4vw, 2.1rem)', lineHeight: 1.2, color: brand.ink },
    h3: { fontFamily: HEADING_FONT, fontWeight: 600, fontSize: 'clamp(1.35rem, 3.2vw, 1.7rem)', lineHeight: 1.25, color: brand.ink },
    h4: { fontFamily: HEADING_FONT, fontWeight: 600, fontSize: 'clamp(1.2rem, 2.6vw, 1.4rem)', lineHeight: 1.3, color: brand.ink },
    h5: { fontFamily: BODY_FONT, fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.35, color: brand.ink },
    h6: { fontFamily: BODY_FONT, fontWeight: 600, fontSize: '1rem', lineHeight: 1.4, color: brand.ink },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 500, color: brand.inkSoft },
    body1: { fontSize: '0.95rem', lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: 0.1 },
    caption: { fontSize: '0.78rem', color: brand.inkSoft },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.cream,
          WebkitFontSmoothing: 'antialiased',
        },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        '*::-webkit-scrollbar-thumb': { backgroundColor: brand.creamDark, borderRadius: 8 },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 1px 12px rgba(35, 33, 34, 0.10)',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          textTransform: 'none',
          fontWeight: 600,
          paddingTop: 8,
          paddingBottom: 8,
          minHeight: 40,
        },
        sizeLarge: { minHeight: 48, fontSize: '1rem' },
        sizeSmall: { minHeight: 34 },
        containedPrimary: {
          backgroundColor: brand.gold,
          color: '#FFFFFF',
          boxShadow: shadow.gold,
          '&:hover': { backgroundColor: brand.goldDark, boxShadow: shadow.gold },
        },
        outlinedPrimary: {
          borderColor: brand.gold,
          color: brand.goldDark,
          borderWidth: 1.5,
          '&:hover': { borderColor: brand.goldDark, borderWidth: 1.5, backgroundColor: 'rgba(199, 164, 110, 0.08)' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radius.md },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          border: `1px solid ${brand.border}`,
          boxShadow: shadow.sm,
          backgroundImage: 'none',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: radius.lg },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: radius.lg },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: radius.pill, fontWeight: 500 },
        colorPrimary: { backgroundColor: brand.gold, color: '#FFFFFF' },
        outlined: { borderColor: brand.gold, color: brand.goldDark },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          '&.Mui-focused fieldset': { borderColor: brand.gold },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root.Mui-focused': { color: brand.goldDark },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottomColor: brand.border },
        head: {
          backgroundColor: brand.goldSoft,
          color: brand.ink,
          fontWeight: 600,
          fontSize: '0.8rem',
          letterSpacing: 0.2,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(199, 164, 110, 0.06)' },
          '&:last-child td': { borderBottom: 0 },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: radius.md },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: brand.ink, fontSize: '0.75rem', borderRadius: radius.sm, padding: '6px 10px' },
        arrow: { color: brand.ink },
      },
    },
    MuiStepper: {
      styleOverrides: {
        root: {
          '& .MuiStepLabel-root .Mui-completed': { color: brand.gold },
          '& .MuiStepLabel-root .Mui-active': { color: brand.goldDark },
          '& .MuiStepConnector-line': { borderColor: brand.creamDark },
          '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: brand.gold },
          '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: brand.gold },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: { backgroundColor: brand.creamDark, borderRadius: radius.pill },
        barColorPrimary: { backgroundColor: brand.gold },
      },
    },
  },
});

export default theme;
